import { HttpClient } from '@angular/common/http';
import { Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { AuthService } from '@app/service/auth.service';
import { IUser, IUserSettings } from '@data/user-profile/user-profile.interface';
import { userRole } from '@data/users/sms-users.interface';
import { environment } from '@env/environment';
import { catchError, shareReplay, map, first, Subject, throwError, Observable, BehaviorSubject } from 'rxjs';

interface IUserCredentialRow {
  user_credentials_id?: string;
  login?: string;
  user_id?: string;
}

interface IUserCredentialNoResult {
  success?: string;
  message?: string;
  result?: string;
}

interface IUserEmailSearchRow {
  email?: string;
  record_num?: string;
  total_records?: string;
}

export interface IOktaEmailOption {
  email: string;
  isDefault: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private _loggedInUserSettings$ = new Subject<IUserSettings>();
  private _pageSize = new BehaviorSubject<number>(200);
  private _cachedLoggedInUserSettings: IUserSettings | null = null;
  private _enhSOASettings: WritableSignal<boolean> = signal(false);

  public setEnhSOASettings(value: boolean): void {
    this._enhSOASettings.set(value);
  }
  public getEnhSOASettings(): Signal<boolean> {
    return this._enhSOASettings;
  }
  
  public setPageSize(size: number): void {
    this._pageSize.next(size);
  }
  public getPageSize(): Observable<number> {
    return this._pageSize.asObservable();
  }

  constructor(
    private _http: HttpClient,
    private _authService: AuthService
  ) { }

  public getUserView(userId: string) {
    return this._http.get<IUser>((environment.api) + "user/" + userId)
      .pipe(
        catchError(error => { return throwError(() => error) })
      );
  }

  public getUserRoles(data: userRole, account_id: string) {
    return this._http.post((environment.api) + "account/" + account_id + "/role/search", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getUserDetailsAndPermissions(userId: string) {
    return this._http.get<IUser>((environment.api) + "user/" + userId + "?return_acl=1", { observe: 'response' })
      .pipe(
        catchError(error => { return throwError(() => error); }),
      );
  }

  public updateExistingUser(data: any, userId: string) {
    return this._http.post((environment.api) + "user/" + userId, data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getUserAccess(userId: string) {
    return this._http.get((environment.api) + "user/" + userId + "/userAccess").pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getUserData(userId: string) {
    return this._http.get((environment.api) + "user/" + userId).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getUserSettings(userId: string) {
    return this._http.get<IUserSettings>((environment.api) + "user/" + userId + "/settings")
      .pipe(
        shareReplay(1),
        catchError((error) => { return throwError(() => error); })
      );
  }

  public updateUserSettings(data: IUserSettings, userId: string) {
    return this._http.post<IUserSettings>((environment.api) + "user/" + userId + "/settings", data)
      .pipe(
        catchError((error) => { return throwError(() => error); })
      );
  }

  public userCredentials(data: any, id: string) {
    return this._http.post((environment.api) + "user/" + id + "/userCredentials/search", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public updateExistingCredentials(userId: string, credentialsId: string, credentialsParams: any) {
    return this._http.post((environment.api) + "user/" + userId + "/userCredentials/" + credentialsId + "/changePassword", credentialsParams).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getOktaCredentialStatus(userId: string): Observable<boolean>  {
    const payload = {
      search_type: 'simple',
      criteria: {
        user_id: {
          type: 'simple',
          value: [userId]
        },
        context: {
          type: 'simple',
          value: ['okta']
        }
      },
      field_list: 'login'
    };

    return this.userCredentials(payload, userId).pipe(
      map((response: unknown) => {
        // If response is array and has items, found
        if (Array.isArray(response) && response.length > 0) {
          return true;
        }
        // If response is object and success = '0', not found
        if ((response as any)?.success === '0') {
          return false;
        }
        // Otherwise, not found
        return false;
      })
  );
}

  public isOktaEmailAvailable(userId: string, email: string): Observable<boolean> {
    const payload = {
      search_type: 'simple',
      criteria: {
        login: {
          type: 'simple',
          value: [email]
        },
        context: {
          type: 'simple',
          value: ['okta']
        }
      },
      field_list: 'login,user_id,user_credentials_id'
    };

    return this.userCredentials(payload, userId).pipe(
      map((response: unknown) => {
        const rows = this._extractUserCredentialRows(response);
        if (!rows.length) {
          return true;
        }

        return !rows.some((item: IUserCredentialRow) => {
          const login = (item.login || '').toLowerCase();
          const requestedEmail = email.toLowerCase();

          return login === requestedEmail && (item.user_id || '') !== userId;
        });
      })
    );
  }

  public searchUserEmails(userId: string, defaultEmail: string = ''): Observable<IOktaEmailOption[]> {
    const payload = {
      search_type: 'simple',
      criteria: {
        user_id: {
          type: 'simple',
          value: [userId]
        }
      },
      include_deleted: 'true',
      field_list: 'email'
    };

    return this._http.post<IUserEmailSearchRow[]>((environment.api) + 'email/search/', payload).pipe(
      map((response) => this._normalizeOktaEmailOptions(response, defaultEmail)),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  public createOktaCredential(userId: string, login: string) {
    var pw = 'NotRealPW#' + Math.floor(Math.random() * (999 - 10 + 1)) + 10;
    const payload = {
      login,
      password: pw,
      context: 'okta'
    };

    return this._http.post((environment.api) + 'user/' + userId + '/userCredentials/', payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }


  private _extractUserCredentialRows(response: unknown): IUserCredentialRow[] {
    if (Array.isArray(response)) {
      return response;
    }

    const noResult = response as IUserCredentialNoResult;

    if (noResult?.success === '0' || noResult?.message === 'No results returned.') {
      return [];
    }

    return [];
  }

  private _normalizeOktaEmailOptions(response: IUserEmailSearchRow[], defaultEmail: string): IOktaEmailOption[] {
    const defaultEmailNormalized = (defaultEmail || '').trim().toLowerCase();
    const uniqueEmails = new Set<string>();

    (response || []).forEach((item) => {
      const email = (item.email || '').trim().toLowerCase();
      if (email) {
        uniqueEmails.add(email);
      }
    });

    const options = Array.from(uniqueEmails).map((email) => ({
      email,
      isDefault: defaultEmailNormalized ? email === defaultEmailNormalized : false
    }));

    return options.sort((a, b) => {
      if (a.isDefault === b.isDefault) {
        return 0;
      }

      return a.isDefault ? -1 : 1;
    });
  }

  public deleteUser(userId: string) {
    return this._http.get((environment.api) + "user/" + userId + "/delete").pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public mimicUser(userId: string) {
    return this._http.get((environment.api) + "user/mimic/" + userId).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public redirectMimicUser(accessToken: string) {
    const form = window.document.createElement("form");
    form.method = 'POST';
    form.action = environment.authClient + "sso_request?t=" + accessToken;

    window.document.body.appendChild(form);
    form.submit();
  }

  public addCarrier(data: any, userId: string) {
    return this._http.post((environment.api) + "user/" + userId + "/usercarrier", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }
  public deleteCarrier(data: any) {
    return this._http.post((environment.api) + "user/usercarrier/deleteSelected", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getAllUsers(data: any) {
    return this._http.post((environment.api) + "user/search", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public getAllMimicUsers(data: any) {
    return this._http.post((environment.api) + "user/mimic/search", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public addNewUser(data: any) {
    return this._http.post((environment.api) + "user", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public accountOwnerNameSearch(data: any) {
    return this._http.post((environment.api) + "user/search", data).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }

  public bulkDeleteUser(userIds: any) {
    return this._http.post((environment.api) + "user/bulkDelete", userIds).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    )
  }
  public userInvite(userId:string){
    return this._http.get((environment.api) + "user/"+userId+"/userInvite/").pipe(
      catchError((error) => {
        return throwError(() => error);
      }))
  }

  public selfServiceConfig(user_id: any, criteria: any) {
    return this._http.post((environment.api) + "user/" + user_id + "/selfServiceConfig/search/", criteria).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public selfServiceConfigUpdate(user_id: any, criteria: any) {
    return this._http.post((environment.api) + "user/" + user_id + "/selfServiceConfig/", criteria).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public userStateLicenseNumber(user_id: any, state_id: any) {
    return this._http.get((environment.api) + "user/" + user_id + "/stateLicenseNumber/" + state_id).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public userCommunicationPreferencePermission(user_id: any) {
    return this._http.get((environment.api) + "user/" + user_id + "/CommunicationPreference/canSendText").pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public userCommunicationPreferenceOption(user_id: any) {
    return this._http.get((environment.api) + user_id + "/communicationPreference/").pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  public userCommunicationPreferenceUpdate(user_id: any, criteria: any) {
    return this._http.post((environment.api) + user_id + "/communicationPreference", criteria).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

}
